import { zodResolver } from "@hookform/resolvers/zod";
import {
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@hortis/ui/breadcrumb";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@hortis/ui/dialog";
import styles from "@hortis/ui/dialog/dialog.module.css";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@hortis/ui/drawer";
import { FormControl, FormField, FormItem } from "@hortis/ui/form";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { Folder, XClose } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@hortis/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { namedOperations } from "generated/graphql";
import { BaseSyntheticEvent, useEffect, useRef, useState } from "react";
import { FormProvider, UseFormReturn, useForm } from "react-hook-form";
import {
  AccessionPicker,
  AccessionPickerLabel,
} from "src/components/pickers/accession-picker/accession-picker";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { DiscardDialog } from "src/features/create-accession/components/accession-data-form/discard-dialog";
import {
  MaterialNestedFields,
  materialDefaultValues,
} from "src/features/create-accession/components/accession-data-form/material-nested-fields";
import { UploadsInProgressDialog } from "src/features/create-accession/components/accession-data-form/uploads-in-progress-dialog";
import { useStagedMaterialPhotos } from "src/features/create-accession/components/accession-data-form/use-staged-material-photos";
import { materialErrorMap } from "src/features/create-accession/components/add-plant-material-form/error-map";
import {
  MaterialSchema,
  createMaterialSchema,
} from "src/features/create-accession/components/add-plant-material-form/schema";
import { processPlantMaterialFormData } from "src/features/create-accession/utils/process-form-data";
import { createMaterialDocument } from "src/features/records/accession/graphql";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { create } from "zustand";

interface CreateMaterialState {
  initialData: Partial<z.infer<typeof createMaterialSchema>> | null;
  createMaterialDialogOpen: boolean;
  setCreateMaterialDialogOpen: (
    open: boolean,
    initialData?: Partial<z.infer<typeof createMaterialSchema>> | null,
  ) => void;
}

export const useCreateMaterialStore = create<CreateMaterialState>((set) => ({
  initialData: null,
  createMaterialDialogOpen: false,
  setCreateMaterialDialogOpen: (open, initialData) => {
    if (!open) {
      set({ createMaterialDialogOpen: open, initialData: null });
    } else {
      set({ createMaterialDialogOpen: open, initialData });
    }
  },
}));

export function CreateMaterialDialog({ siteSlug }: { siteSlug: string }) {
  const {
    initialData,
    createMaterialDialogOpen: open,
    setCreateMaterialDialogOpen: onOpenChange,
  } = useCreateMaterialStore();
  const isDesktop = useMediaQuery("md");
  const navigate = useNavigate();
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const taxonPickerRef = useRef<HTMLButtonElement | null>(null);
  const {
    stagedPhotos,
    setStagedPhotos,
    uploadsInProgressDialogOpen,
    setUploadsInProgressDialogOpen,
    processStagedPhotos,
  } = useStagedMaterialPhotos();

  const defaultValues = (): Partial<z.infer<typeof createMaterialSchema>> => {
    if (initialData != null) {
      const { images: _, ...rest } = initialData;
      return { ...materialDefaultValues, ...rest };
    }
    return materialDefaultValues;
  };
  const form = useForm<z.infer<typeof createMaterialSchema>>({
    defaultValues: defaultValues(),
    criteriaMode: "all",
    resolver: zodResolver(
      createMaterialSchema,
      { async: true, errorMap: materialErrorMap },
      { mode: "async" },
    ),
  });

  const createMaterial = useGqlMutation({
    mutation: createMaterialDocument,
    onError: () => {
      toast.error("Failed to create material");
    },
    onSuccess: (res) => {
      if (res.createPlantMaterial?.__typename === "GenericError") {
        toast.error("Failed to create material");
      } else {
        const accessionNumber =
          res.createPlantMaterial?.accession?.accessionNumber;
        const qualifier = res.createPlantMaterial?.qualifier;
        const materialNumber =
          accessionNumber == null || qualifier == null
            ? null
            : createMaterialNumber(accessionNumber, qualifier, "_");
        if (materialNumber != null) {
          toast.success("Material created", {
            description: materialNumber.replaceAll("_", "/"),
            action: {
              label: "View",
              onClick: () =>
                navigate({
                  to: "/site/$siteSlug/material/$materialNumber",
                  params: { materialNumber, siteSlug },
                }),
            },
          });
        }
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetAccession],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.PlantMaterials],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetDashboard],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.MapListMaterials],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetLocationByName],
        });
        void queryClient.invalidateQueries({
          queryKey: [namedOperations.Query.GetLocation],
        });
        onOpenChange(false);
      }
    },
  });

  const handleSubmit = async (e?: BaseSyntheticEvent) => {
    const processedPhotos = processStagedPhotos();
    if (processedPhotos == null) {
      return;
    }
    form.setValue("images", processedPhotos);
    void form.handleSubmit(
      async (data: z.infer<typeof createMaterialSchema>) => {
        const { accession, ...rest } = data;
        await createMaterial.mutateAsync({
          accessionId: accession.id,
          plantMaterial: processPlantMaterialFormData({
            ...rest,
          }),
        });
      },
    )(e);
  };

  const handleOpenChange = (open: boolean) => {
    if (
      Object.keys(form.formState.dirtyFields).length === 0 ||
      discardDialogOpen
    ) {
      onOpenChange(open);
    } else {
      setDiscardDialogOpen(true);
    }
  };

  useEffect(() => {
    if (open) {
      form.reset(defaultValues());
    }
    setDiscardDialogOpen(false);
  }, [open, form]);

  const content = (
    <TooltipProvider delayDuration={1000}>
      <UploadsInProgressDialog
        open={uploadsInProgressDialogOpen}
        onOpenChange={setUploadsInProgressDialogOpen}
        onDiscard={() => {
          handleSubmit();
        }}
      />
      <div className="absolute right-5 top-5 gap-1 flex items-center">
        <Tooltip>
          <DiscardDialog
            onDiscard={() => {
              handleOpenChange(false);
            }}
            open={discardDialogOpen}
            onOpenChange={setDiscardDialogOpen}
          >
            <TooltipTrigger asChild>
              <IconButton
                ariaLabel="Close"
                variant="tertiaryGray"
                testId="close-create-accession-dialog"
                className="p-1.5"
                size="sm"
                icon={XClose}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenChange(false);
                }}
                onFocusCapture={stopEventPropagation}
              />
            </TooltipTrigger>
          </DiscardDialog>
          <TooltipContent side="bottom" sideOffset={0}>
            Discard material
          </TooltipContent>
        </Tooltip>
      </div>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className="pt-5">
          <BreadcrumbList className="pl-4">
            <FormField
              control={form.control}
              name="accession"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <AccessionPicker
                      data-cy="accession-picker"
                      siteSlug={siteSlug}
                      {...field}
                    >
                      <Button
                        size="xs"
                        startIcon={Folder}
                        className={twMerge(
                          "w-fit",
                          field.value == null && "text-grey-500",
                          fieldState.error &&
                            "border-error-500 text-error-700 hover:border-error-500 hover:bg-error-25 hover:text-error-700",
                        )}
                      >
                        {field.value == null ? (
                          "Accession"
                        ) : (
                          <AccessionPickerLabel value={field.value} />
                        )}
                      </Button>
                    </AccessionPicker>
                  </FormControl>
                </FormItem>
              )}
            />
            <BreadcrumbSeparator />
            <BreadcrumbItem className="text-xs">New material</BreadcrumbItem>
          </BreadcrumbList>
          <MaterialNestedFields
            siteSlug={siteSlug}
            form={form as unknown as UseFormReturn<MaterialSchema>}
            stagedPhotos={stagedPhotos}
            setStagedPhotos={setStagedPhotos}
          />
          <div className="flex justify-end border-t border-grey-100 py-2 pl-3 pr-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  type="submit"
                  size="sm"
                  loading={form.formState.isSubmitting}
                  testId="create-material-button"
                  onFocusCapture={stopEventPropagation}
                  disabled={!open}
                >
                  Create material
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={0}>
                Save material
              </TooltipContent>
            </Tooltip>
          </div>
        </form>
      </FormProvider>
    </TooltipProvider>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={twMerge(
            "top-12 flex max-h-[calc(100%-96px)] w-full max-w-[668px] translate-y-0 flex-col gap-0! rounded-xl bg-white p-0! [@media(min-height:953px)]:top-32 [@media(min-height:953px)]:max-h-[calc(100%-256px)]",
            styles.dialogNoY,
          )}
          onOpenAutoFocus={(e) => {
            e.stopPropagation();
            e.preventDefault();
            taxonPickerRef.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">Create material</DialogTitle>
          <DialogDescription className="sr-only">
            Create material form
          </DialogDescription>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} dismissible={false}>
      <DrawerContent
        className="h-full max-h-full min-h-full"
        onOpenAutoFocus={(e) => {
          e.stopPropagation();
          e.preventDefault();
          taxonPickerRef.current?.focus();
        }}
      >
        <DrawerTitle className="sr-only">Create material</DrawerTitle>
        <DrawerDescription className="sr-only">
          Create material form
        </DrawerDescription>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
